import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Learn from "@/pages/Learn";
import CourseDetail from "@/pages/CourseDetail";
import Article from "@/pages/Article";
import Community from "@/pages/Community";
import Resources from "@/pages/Resources";
import Directory from "@/pages/Directory";
import Events from "@/pages/Events";
import Certification from "@/pages/Certification";
import LegalCenter from "@/pages/LegalCenter";
import ExternalNews from "@/pages/ExternalNews";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";

import "@/App.css";
import { PUBLIC_LAUNCH } from "@/constants/launch";
import PublicInfo from "@/components/shared/PublicInfo";
import { MemberProvider, useMember } from "@/context/MemberContext";
import { MemberGuard, MembershipEntry, MemberCallback, MemberDashboard, MyApps, MyLearning, UpcomingClasses, SavedResources, MyActivities, MemberSettings } from "@/pages/MemberPages";
import IntroCourse, { ArticleLearningCheck } from "@/pages/CoursePages";
import LegalResources from "@/pages/LegalResources";
import { FreeApps, AboutVillage, ResourceLibrary, CommunityEvents, CertificateOverview, ApprovedInformation, CompanionDirectory } from "@/pages/V34PublicPages";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-muted-foreground">
        Loading your village…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const { user: member } = useMember();
  const location = useLocation();
  // Read from useLocation().hash (reactive) — NOT window.location.hash
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/member-callback" element={<MemberCallback />} />
        <Route path="/dashboard/apps" element={<MemberGuard><MyApps /></MemberGuard>} />
        <Route path="/dashboard/learning" element={<MemberGuard><MyLearning /></MemberGuard>} />
        <Route path="/dashboard/classes" element={<MemberGuard><UpcomingClasses /></MemberGuard>} />
        <Route path="/dashboard/saved" element={<MemberGuard><SavedResources /></MemberGuard>} />
        <Route path="/dashboard/activities" element={<MemberGuard><MyActivities /></MemberGuard>} />
        <Route path="/dashboard/settings" element={<MemberGuard><MemberSettings /></MemberGuard>} />
        <Route path="/learn/introduction-canine-cognitive-development" element={<IntroCourse key={member?.user_id || "guest"} />} />
        <Route path="/learn/article-learning-check" element={<MemberGuard><ArticleLearningCheck /></MemberGuard>} />
        <Route path="/verify-email" element={<PublicInfo title="One more step: verify your email" description="Continue the secure membership connection in the companion app to verify your email, then return to My Wolfdog Village." />} />
        <Route path="/forgot-password" element={<PublicInfo title="Reset your password" description="Use your existing companion-app account recovery page." />} />

        <Route path="/free-apps" element={<FreeApps />} />
        <Route path="/breeder-standards" element={<ApprovedInformation page="24_breeder_standards" title="Breeder Directory Standards & Disclosures" />} />
        <Route path="/register" element={<MembershipEntry register />} />
        <Route path="/learn/articles/:slug" element={<Article />} />
        <Route path="/learn/:courseId" element={PUBLIC_LAUNCH ? <PublicInfo title="Courses are being prepared" description="Please explore our free Learning Center articles while our courses are being prepared." /> : <CourseDetail />} />
        <Route path="/community" element={<CommunityEvents />} />
        <Route path="/resources" element={<ResourceLibrary />} />
        <Route path="/directory" element={<CompanionDirectory />} />
        <Route path="/directory/*" element={<CompanionDirectory />} />
        <Route path="/affiliated-breeders/*" element={<CompanionDirectory application />} />
        <Route path="/events" element={<Navigate to="/community" replace />} />
        <Route path="/certification" element={<CertificateOverview />} />
        <Route path="/legal" element={<LegalResources />} />
          <Route path="/news" element={<ExternalNews />} />
        <Route path="/about" element={<AboutVillage />} />
          <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<MembershipEntry />} />
        <Route path="/dashboard" element={<MemberGuard><MemberDashboard /></MemberGuard>} />
        <Route path="*" element={<div className="container-x py-32 text-center"><h1 className="font-display text-6xl">404</h1><p className="mt-4 text-muted-foreground">This trail leads nowhere.</p></div>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MemberProvider><BrowserRouter>
          <AppRouter />
          <Toaster richColors position="top-center" />
        </BrowserRouter></MemberProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
